import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { usePrevious } from 'hooks/usePrevious';
import { isEqual } from 'es-toolkit/compat';
import { useEffect } from 'react';
import { useAuthState } from 'state/useAuthState';

/**
 * Invalidates a query when the authenticated user's data changes.
 *
 * @param queryKey - Must be memoized or a constant to prevent unnecessary
 * effect runs. Define outside component if static, or wrap in useMemo if dynamic.
 */
export const useInvalidateQueryOnAuthChange = (queryKey: QueryKey): void => {
  const queryClient = useQueryClient();
  const { user } = useAuthState();
  const prevUser = usePrevious(user);

  useEffect(() => {
    // Invalidate only when user data changes while authenticated
    // Skip on initial mount and login/logout (app refresh handles those)
    if (prevUser && user && !isEqual(prevUser, user)) {
      const queryState = queryClient.getQueryState(queryKey);
      // Don't invalidate if query is currently loading
      if (queryState?.fetchStatus !== 'fetching') {
        queryClient.invalidateQueries({ queryKey });
      }
    }
  }, [queryClient, queryKey, prevUser, user]);
};
